import { I18nProvider } from '@lingui/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { i18n } from './i18n'
import './index.css'
import {
  getProperties,
  initializeFaviconAndTitle,
  initializeI18n
} from './core/utils/init-app'
import * as serviceWorker from './serviceWorker'
import {
  hideGlobalLoading,
  showGlobalLoading
} from './core/components/global-loading'
import { ServerOfflineScreen } from './core/components/server-offline-screen'

import { initApollo } from './core/utils/apollo'
import { initAnalytics } from './core/utils/analytics'
import { ApolloProvider } from '@apollo/client'
import { logout } from './core/utils/logout-keycloack'
import { NuqsAdapter } from 'nuqs/adapters/react'
import {
  AxiosMenuProvider,
  AxiosPortalProvider,
  client,
  initAxios,
  initKeycloak,
  KeycloakProvider
} from '@tecsinapse-org/portal-menu-kit'
import { QueryClientProvider } from '@tanstack/react-query'
import { PrivateApp } from './PrivateApp'
import { read, STORES } from '@/core/data/storage'
import { KeycloakConfig } from 'keycloak-js'
import PreLogin from '@/modules/pre-login/ui'
import { inferRealm } from '@/core/data/keycloak'
import { setAndRedirect } from '@/modules/pre-login/utils/functions'

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')
const root = createRoot(rootElement)
const isDev = import.meta.env.VITE_PROFILE === 'development'

async function preLogin() {
  try {
    const config = await inferRealm()
    setAndRedirect(config)
  } catch (e) {
    root.render(
      <I18nProvider i18n={i18n}>
        <PreLogin />
      </I18nProvider>
    )
  }
}

async function initApp() {
  showGlobalLoading()
  try {
    const config = read<KeycloakConfig>(STORES.KEYCLOAK)

    if (!config) {
      preLogin().then()
      return
    }

    const { properties, manifest } = await getProperties()

    const kcInstance = await initKeycloak({
      url: import.meta.env.VITE_AUTH_URL,
      clientId: String(config?.clientId),
      realm: String(config?.realm)
    })

    if (!kcInstance.authenticated) {
      localStorage.clear()
      sessionStorage.clear()
      location.reload()
      return
    }

    const axios = initAxios(`${import.meta.env.VITE_API_URL}`, kcInstance)
    const menuAxios = initAxios(
      `${import.meta.env.VITE_MENU_API_URL}`,
      kcInstance,
      {
        'x-menu-only': true
      }
    )
    const apolloClient = initApollo(
      `${import.meta.env.VITE_API_URL}/api/graphql`,
      kcInstance
    )

    await initializeI18n(apolloClient)
    initializeFaviconAndTitle(manifest)
    initAnalytics(!isDev ? properties.portalAnalyticsCode : undefined)

    if (kcInstance.authenticated) {
      await serviceWorker.register(
        properties.portalVapidPublicKey,
        apolloClient
      )
    }

    root.render(
      <ApolloProvider client={apolloClient}>
        <KeycloakProvider
          keycloak={kcInstance}
          logout={async () => logout(apolloClient)}
        >
          <AxiosPortalProvider instance={axios}>
            <AxiosMenuProvider instance={menuAxios}>
              <QueryClientProvider client={client}>
                <NuqsAdapter>
                  <I18nProvider i18n={i18n}>
                    {kcInstance.authenticated ? <PrivateApp /> : null}
                  </I18nProvider>
                </NuqsAdapter>
              </QueryClientProvider>
            </AxiosMenuProvider>
          </AxiosPortalProvider>
        </KeycloakProvider>
      </ApolloProvider>
    )
  } catch (e) {
    console.error(e)
    root.render(<ServerOfflineScreen />)
  } finally {
    hideGlobalLoading()
  }
}

initApp().catch(console.error)
